/**
 * Audio assembler utility for converting raw PCM chunks to WAV format.
 *
 * The live recording stores audio as raw PCM (16kHz, mono, 16-bit) chunks in IndexedDB.
 * Before uploading to the transcription service, these chunks must be assembled
 * into a proper WAV file with RIFF headers.
 *
 * WAV format specification:
 * - RIFF header (12 bytes)
 * - Format chunk (24 bytes)
 * - Data chunk header (8 bytes)
 * - PCM audio data
 */
import { CAPTURE_CHANNEL_COUNT, CAPTURE_SAMPLE_RATE } from '@/utils/audioCaptureProfiles'

// Audio format constants. The rate and channel count are the *same* values the
// capture pipeline constrains itself to — imported rather than repeated, so the
// header can never drift away from what was actually recorded (#3285).
const SAMPLE_RATE = CAPTURE_SAMPLE_RATE
const NUM_CHANNELS = CAPTURE_CHANNEL_COUNT
const BITS_PER_SAMPLE = 16 // 16-bit
const BYTES_PER_SAMPLE = BITS_PER_SAMPLE / 8

/**
 * Concatenates multiple PCM blobs into a single ArrayBuffer.
 *
 * @param chunks - Array of PCM audio blobs in sequence order
 * @returns Single ArrayBuffer containing all PCM data
 */
async function concatenatePcmChunks(chunks: Blob[]): Promise<ArrayBuffer> {
  // Calculate total size
  const totalSize = chunks.reduce((sum, chunk) => sum + chunk.size, 0)
  const result = new Uint8Array(totalSize)

  // Copy each chunk into the result buffer
  let offset = 0
  for (const chunk of chunks) {
    const chunkData = new Uint8Array(await chunk.arrayBuffer())
    result.set(chunkData, offset)
    offset += chunkData.length
  }

  return result.buffer
}

/**
 * Creates a WAV file header for the given PCM data.
 *
 * WAV file structure:
 * - Bytes 0-3: "RIFF" magic number
 * - Bytes 4-7: File size minus 8 (i.e., size of "RIFF" + file size field)
 * - Bytes 8-11: "WAVE" format identifier
 * - Bytes 12-15: "fmt " subchunk identifier
 * - Bytes 16-19: Subchunk1 size (16 for PCM)
 * - Bytes 20-21: Audio format (1 for PCM)
 * - Bytes 22-23: Number of channels
 * - Bytes 24-27: Sample rate
 * - Bytes 28-31: Byte rate (SampleRate * NumChannels * BitsPerSample/8)
 * - Bytes 32-33: Block align (NumChannels * BitsPerSample/8)
 * - Bytes 34-35: Bits per sample
 * - Bytes 36-39: "data" subchunk identifier
 * - Bytes 40-43: Data size
 * - Bytes 44+: Audio data
 *
 * @param pcmDataSize - Size of the PCM audio data in bytes
 * @returns ArrayBuffer containing the 44-byte WAV header
 */
function createWavHeader(pcmDataSize: number): ArrayBuffer {
  const header = new ArrayBuffer(44)
  const view = new DataView(header)

  const byteRate = SAMPLE_RATE * NUM_CHANNELS * BYTES_PER_SAMPLE
  const blockAlign = NUM_CHANNELS * BYTES_PER_SAMPLE

  // RIFF chunk descriptor
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + pcmDataSize, true) // File size - 8
  writeString(view, 8, 'WAVE')

  // Format subchunk
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true) // AudioFormat (1 = PCM)
  view.setUint16(22, NUM_CHANNELS, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, BITS_PER_SAMPLE, true)

  // Data subchunk
  writeString(view, 36, 'data')
  view.setUint32(40, pcmDataSize, true)

  return header
}

/**
 * Writes a string to a DataView at the specified offset.
 */
function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}

/**
 * Assembles raw PCM chunks into a WAV file.
 *
 * Takes an array of PCM audio blobs (stored in IndexedDB during recording)
 * and combines them into a single WAV file suitable for upload to the
 * transcription service.
 *
 * @param chunks - Array of PCM audio blobs in sequence order
 * @returns Complete WAV file as a Blob
 * @throws Error if no chunks are provided
 *
 * @example
 * ```typescript
 * const chunks = await storage.getSessionChunks(sessionId)
 * const wavBlob = await assembleWav(chunks.map(c => c.blob))
 * // Upload wavBlob to transcription service
 * ```
 */
export async function assembleWav(chunks: Blob[]): Promise<Blob> {
  if (chunks.length === 0) {
    throw new Error('No audio chunks to assemble')
  }

  // Concatenate all PCM chunks
  const pcmData = await concatenatePcmChunks(chunks)

  // Create WAV header
  const header = createWavHeader(pcmData.byteLength)

  // Combine header and PCM data into WAV blob
  return new Blob([header, pcmData], { type: 'audio/wav' })
}

/**
 * Generates a filename for a recording based on timestamp.
 *
 * @param timestamp - Recording timestamp (defaults to current time)
 * @returns Filename in format "recording-{ISO timestamp}.wav"
 *
 * @example
 * ```typescript
 * const filename = generateRecordingFilename()
 * // "recording-2026-03-19T14-30-00.wav"
 * ```
 */
export function generateRecordingFilename(timestamp: number = Date.now()): string {
  const date = new Date(timestamp)
  // Format: YYYY-MM-DDTHH-mm-ss (ISO-like but with hyphens for file system compatibility)
  const isoString = date.toISOString().replace(/:/g, '-').split('.')[0]
  return `recording-${isoString}.wav`
}
